import React, {
  createContext,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from "react";
import { toast } from "sonner";
import {
  fetchAuthTokens,
  fetchLocalAuthTokens,
  isAccessTokenExpired,
  logOut as logOutReq,
  tokenRemover,
} from "../actions/auth";

type ContextAuth = {
  RefreshToken: string | null;
  AccessToken: string | null;
  ExpiresIn: number | null;
  IdToken: string | null;
};

type AuthContextType = {
  isAuthenticated: boolean | ContextAuth | undefined;
  logout: () => void;
  setIsAuthenticated: React.Dispatch<React.SetStateAction<any>>;
};

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined
);

type AuthProviderProps = {
  children: ReactNode;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<
    boolean | ContextAuth | undefined
  >(undefined);

  const checkAuthentication = async () => {
    try {
      const tokens = fetchLocalAuthTokens();
      if (tokens.ExpiresIn && isAccessTokenExpired(tokens.ExpiresIn)) {
        try {
          const newTokens = await fetchAuthTokens();
          setIsAuthenticated(newTokens);
        } catch (err) {
          setIsAuthenticated(false);
        }
      } else {
        setIsAuthenticated(tokens.AccessToken ? tokens : false);
      }
    } catch (error) {
      setIsAuthenticated(false);
    }
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem("AccessToken") ?? "";
      const RefreshToken = localStorage.getItem("RefreshToken") ?? "";
      tokenRemover();
      setIsAuthenticated(false);
      logOutReq({ token, RefreshToken }).catch((err) => console.error(err));
    } catch (error) {
      toast.error("Logout failed");
    }
  };

  useEffect(() => {
    checkAuthentication();
  }, []);

  const contextValue = useMemo(
    () => ({
      isAuthenticated,
      logout,
      setIsAuthenticated,
    }),
    [isAuthenticated]
  );
  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};
